import { HttpError, Logger } from '@asgard/ui-common';
import { zodResolver } from '@hookform/resolvers/zod';
import { useI18n } from 'hooks/useI18n';
import { useToasts } from 'hooks/useToasts';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { ServiceDeal } from 'services/service.deal';
import ServiceEventTracking from 'services/service.event-tracking';
import {
  CompaniesFormFieldsValidationSchema,
  FORM_FIELDS_DEFAULT_VALUES
} from './YourOfferSalaryFormContact.constants';
import {
  CompaniesFormFields,
  UseCompaniesForm
} from './YourOfferSalaryFormContact.types';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

export const useCompaniesForm: UseCompaniesForm = () => {
  const { t } = useI18n('your-offer-salary');
  const { locale } = useRouter();
  const { addErrorToast, addSuccessToast } = useToasts();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const formMethods = useForm<CompaniesFormFields>({
    defaultValues: FORM_FIELDS_DEFAULT_VALUES,
    resolver: zodResolver(CompaniesFormFieldsValidationSchema)
  });

  const isTermsAccepted = !!formMethods.watch('terms');

  const onSubmit = useCallback(
    async (data: CompaniesFormFields) => {
      try {
        if (!executeRecaptcha) {
          console.error('Recaptcha not available');
          return;
        }

        const recaptchaToken = await executeRecaptcha('your_offer_salary_form');
        await ServiceDeal.create({
          ...data,
          howDoYouKnowUs: 'YOUR-OFFER-SALARY-LANDING',
          source: 'Manfred Web',
          consultingOptions: [],
          recaptchaToken
        });

        formMethods.reset(FORM_FIELDS_DEFAULT_VALUES);

        addSuccessToast(
          t('your-offer-salary.form-contact.success'),
          t('your-offer-salary.form-contact.success_title')
        );
      } catch (ex) {
        Logger.error({
          context: 'CompaniesForm',
          message: `There was an error while submitting the Companies form`,
          error: ex as HttpError,
          object: data
        });

        addErrorToast(
          t('your-offer-salary.form-contact.error'),
          t('your-offer-salary.form-contact.error_title')
        );
      }
    },
    [addErrorToast, addSuccessToast, formMethods, executeRecaptcha, t]
  );

  const onFormFieldFocus = useCallback(() => {
    ServiceEventTracking.track({
      type: 'Landing - Form Focused',
      properties: {
        objectName: 'contacto-your-offer-salary-landing',
        url: window.location.href,
        urlParams: new URLSearchParams(window.location.search).toString()
      }
    });
  }, []);

  return {
    t,
    locale,
    formMethods,
    onSubmit,
    isTermsAccepted,
    onFormFieldFocus
  };
};
